<template>
  <LoadingWithText v-if="isLoading" />
  <div v-else>
    <div class="intro-y flex flex-col sm:flex-row items-center mt-8">
      <PageHeader class="text-lg font-medium mr-auto" :title="title" />
      <div class="w-full sm:w-auto flex mt-4 sm:mt-0">
        <Button
          type="button"
          :to="{ name: 'Customers' }"
          class="box text-gray-700 dark:text-gray-300 mr-2 flex"
        >
          {{ $t('text.back') }}
        </Button>
        <Button
          type="button"
          @click="onSubmit"
          :loading="isLoading"
          class="w-24 bg-theme-1 text-white"
        >
          {{ $t('text.save') }}
        </Button>
      </div>
    </div>
    <div class="intro-y mt-5">
      <form>
        <div class="grid grid-cols-12 gap-6">
          <div class="intro-y col-span-12 sm:col-span-12 lg:col-span-4 xl:col-span-4">
            <CustomerFormMain :form="form" :v-form="$v.form" :degree-list="degreeList" />
          </div>
          <div class="intro-y col-span-12 sm:col-span-12 lg:col-span-4 xl:col-span-4">
            <CustomerFormFinance
              :form="form"
              :v-form="$v.form"
              :job-types="jobTypeList"
              :job-positions="jobPositionTypeList"
              :salary-range="salaryRange"
              :loan-range="loanRange"
            />
          </div>
          <div class="intro-y col-span-12 sm:col-span-12 lg:col-span-4 xl:col-span-4">
            <CustomerFormBank :form="form" :v-form="$v.form" :banks="bankList" class="intro-y" />
            <CustomerFormAddress
              :form="form"
              :v-form="$v.form"
              :cities="cityList"
              class="intro-y mt-5"
            />
            <CustomerFormEmergencyContacts
              :form="contacts"
              :v-form="$v.contacts"
              :relationshipList="relationshipTypeList"
              class="intro-y mt-5"
              @add="addContact"
              @delete="deleteContact"
            />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { assign } from 'lodash';
import { CUSTOMER_ADD, CUSTOMER_UPDATE, CUSTOMER_REQUEST } from '@/store/actions/customer';
import {
  CITY_LIST_REQUEST,
  HOUSE_TYPE_LIST_REQUEST,
  DEGREE_LIST_REQUEST,
  JOB_TYPE_LIST_REQUEST,
  JOB_POSITION_TYPE_LIST_REQUEST,
  BANK_LIST_REQUEST,
  RELATIONSHIP_TYPE_LIST_REQUEST,
} from '@/store/actions/other';
import { validationMixin } from 'vuelidate';
import { required, minLength, email, numeric } from 'vuelidate/lib/validators';
import Button from '@/components/ui/button/Button';
import PageHeader from '@/components/ui/PageHeader';
import CustomerFormMain from '@/components/customer/CustomerFormMain';
import CustomerFormAddress from '@/components/customer/CustomerFormAddress';
import CustomerFormFinance from '@/components/customer/CustomerFormFinance';
import CustomerFormBank from '@/components/customer/CustomerFormBank';
import CustomerFormEmergencyContacts from '@/components/customer/CustomerFormEmergencyContacts';

export default {
  mixins: [validationMixin],

  components: {
    Button,
    PageHeader,
    CustomerFormMain,
    CustomerFormAddress,
    CustomerFormFinance,
    CustomerFormBank,
    CustomerFormEmergencyContacts,
  },

  props: {
    business: Object,
  },

  validations: {
    form: {
      familyName: {
        required,
        minLength: minLength(2),
      },
      lastName: {
        required,
        minLength: minLength(2),
      },
      firstName: {
        required,
        minLength: minLength(2),
      },
      registrationNumber: {
        required,
        minLength: minLength(2),
      },
      phone: {
        required,
        numeric,
        phoneNumber: phone => {
          return phone.length === 8;
        },
      },
      email: {
        required,
        email,
      },
      facebookAddress: {
        required,
        minLength: minLength(2),
      },
      degreeId: {
        required,
      },

      cityId: {
        required,
      },
      districtId: {
        required,
      },
      horooId: {
        required,
      },
      buildingStreet: {
        required,
      },
      doorNumber: {
        required,
      },
      houseTypeId: {
        required,
      },
      monthlySalary: {
        required,
      },
      jobTypeId: {
        required,
      },
      companyName: {
        required,
      },
      positionTypeId: {
        required,
      },
      bank: {
        required,
        id: {
          required,
        },
        accountHolder: {
          required,
          minLength: minLength(2),
        },
        accountNumber: {
          required,
        },
      },
      amount: {
        required,
      },
    },
    contacts: {
      required,
      $each: {
        required,
        relationshipTypeId: {
          required,
        },
        name: {
          required,
        },
        phone: {
          required,
        },
      },
    },
  },

  data() {
    return {
      form: {
        familyName: '',
        lastName: '',
        firstName: '',
        registrationNumber: '',
        phone: '',
        email: '',
        facebookAddress: '',
        isMarried: 0,
        familySize: 1,
        degreeId: null,

        cityId: null,
        districtId: null,
        horooId: null,
        buildingStreet: '',
        doorNumber: '',
        registeredOnAddress: 0,
        timeOnAddress: 1,
        houseTypeId: null,
        isHouseOwner: 0,

        isLoan: 0,
        leasingPaymentMonth: 0,
        isCar: 0,
        monthlySalary: 0,
        jobTypeId: null,
        totalYearWork: 0,
        companyName: '',
        totalYearWorkingCompany: 0,
        positionTypeId: null,

        bank: {
          id: null,
          accountHolder: '',
          accountNumber: '',
        },
        amount: 0,
      },
      salaryRange: [],
      loanRange: [],
      contacts: [
        { relationshipTypeId: null, name: '', phone: '' },
        { relationshipTypeId: null, name: '', phone: '' },
      ],
    };
  },

  computed: {
    ...mapGetters([
      'customerStatus',
      'customer',
      'cityList',
      'houseTypeList',
      'degreeList',
      'jobTypeList',
      'jobPositionTypeList',
      'bankList',
      'relationshipTypeList',
    ]),
    isLoading() {
      return this.customerStatus === 'loading';
    },
    title() {
      return this.$t('nav.customerUpdate');
    },
  },

  async created() {
    await this.fetchData();
    this.$store.dispatch(CITY_LIST_REQUEST);
    this.$store.dispatch(HOUSE_TYPE_LIST_REQUEST);
    this.$store.dispatch(DEGREE_LIST_REQUEST);
    this.$store.dispatch(JOB_TYPE_LIST_REQUEST);
    this.$store.dispatch(JOB_POSITION_TYPE_LIST_REQUEST);
    this.$store.dispatch(BANK_LIST_REQUEST);
    this.$store.dispatch(RELATIONSHIP_TYPE_LIST_REQUEST);
  },

  methods: {
    async fetchData() {
      if (this.$route.params.customerId) {
        await this.$store.dispatch(CUSTOMER_REQUEST, this.$route.params.customerId);

        if (this.customer) {
          this.form = {
            familyName: this.customer.familyName,
            lastName: this.customer.lastName,
            firstName: this.customer.firstName,
            registrationNumber: this.customer.registrationNumber,
            phone: this.customer.phone,
            email: this.customer.email,
            facebookAddress: this.customer.facebookAddress,
            isMarried: this.customer.isMarried,
            familySize: this.customer.familySize,
            degreeId: this.customer.degreeId,

            cityId: this.customer.cityId,
            districtId: this.customer.districtId,
            horooId: this.customer.horooId,
            buildingStreet: this.customer.buildingStreet,
            doorNumber: this.customer.doorNumber,
            registeredOnAddress: this.customer.registeredOnAddress,
            timeOnAddress: this.customer.timeOnAddress,
            houseTypeId: this.customer.houseTypeId,
            isHouseOwner: this.customer.isHouseOwner,

            isLoan: this.customer.isLoan,
            leasingPaymentMonth: this.customer.leasingPaymentMonth,
            isCar: this.customer.isCar,
            monthlySalary: this.customer.monthlySalary,
            jobTypeId: this.customer.jobTypeId,
            totalYearWork: parseInt(this.customer.totalYearWork, 10),
            companyName: this.customer.companyName,
            totalYearWorkingCompany: this.customer.totalYearWorkingCompany,
            positionTypeId: this.customer.positionTypeId,

            bank: {
              id: this.customer.banks.length > 0 ? this.customer.banks[0].bankId : null,
              accountHolder:
                this.customer.banks.length > 0 ? this.customer.banks[0].accountHolder : '',
              accountNumber:
                this.customer.banks.length > 0 ? this.customer.banks[0].accountNumber : '',
            },
            amount: this.customer.amount,

            contacts:
              this.customer.emergencyContacts.length > 0
                ? this.customer.emergencyContacts
                : [{ relationshipTypeId: null, name: '', phone: '' }],
          };
          this.loanRange = this.customer.loanRange;
          this.salaryRange = this.customer.salaryRange;
        }
        if (this.customer.emergencyContacts.length > 0) {
          this.contacts = this.customer.emergencyContacts.map(item => {
            return { ...item };
          });
        }
      }
    },

    addContact() {
      this.contacts.push({
        relationshipTypeId: null,
        name: '',
        phone: '',
      });
    },

    deleteContact(index) {
      this.contacts.splice(index, 1);
    },

    async onSubmit() {
      if (this.$v) {
        this.$v.$touch();
        if (this.$v.form.$anyError || this.$v.contacts.$anyError) {
          this.$toasted.show(this.$t('toast.failedForm'), { type: 'error' });
          return;
        }
      }

      try {
        assign(this.form, { contacts: this.contacts });
        if (this.$route.params.customerId) {
          const payload = {
            id: this.$route.params.customerId,
            data: this.form,
          };
          await this.$store.dispatch(CUSTOMER_UPDATE, payload);
        } else {
          const payload = {
            data: this.form,
          };
          await this.$store.dispatch(CUSTOMER_ADD, payload);
        }

        if (this.customerStatus === 'success') {
          this.$router.push({ name: 'Customers' });
          this.$toasted.show(this.$t('toast.success'), { type: 'success' });
        }
      } catch (error) {
        this.$toasted.show(error.message, { type: 'error' });
      }
    },
  },
};
</script>
